import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Users, 
    Clock, 
    CheckCircle, 
    XCircle, 
    TrendingUp,
    ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getDashboardStats } from '@/lib/api';
import { cn } from '@/lib/utils';

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await getDashboardStats();
                setStats(data);
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStats();
    }, []);

    const statCards = [
        {
            title: 'Total Applications',
            value: stats?.overview?.total || 0,
            icon: Users,
            color: 'text-primary',
            bgColor: 'bg-primary/10'
        },
        {
            title: 'Pending Review',
            value: stats?.overview?.pending || 0,
            icon: Clock,
            color: 'text-warning',
            bgColor: 'bg-warning/10'
        },
        {
            title: 'Approved',
            value: stats?.overview?.approved || 0,
            icon: CheckCircle,
            color: 'text-success',
            bgColor: 'bg-success/10'
        },
        {
            title: 'Rejected',
            value: stats?.overview?.rejected || 0,
            icon: XCircle,
            color: 'text-destructive',
            bgColor: 'bg-destructive/10'
        }
    ];

    const statusColors = {
        pending: 'status-pending',
        approved: 'status-accepted',
        rejected: 'status-rejected'
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-32 bg-muted animate-pulse rounded-xl" />
                    ))}
                </div>
                <div className="h-96 bg-muted animate-pulse rounded-xl" />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="font-display text-3xl font-bold text-foreground">
                    Dashboard
                </h1>
                <p className="text-muted-foreground mt-1">
                    Welcome back! Here's an overview of Girne MUN 2026 applications.
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat) => (
                    <Card key={stat.title} className="border-border">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground font-medium">
                                        {stat.title}
                                    </p>
                                    <p className="text-3xl font-bold text-foreground mt-2">
                                        {stat.value}
                                    </p>
                                </div>
                                <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", stat.bgColor)}>
                                    <stat.icon className={cn("w-6 h-6", stat.color)} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Applications */}
                <Card className="lg:col-span-2 border-border">
                    <CardHeader className="flex flex-row items-center justify-between pb-4">
                        <CardTitle className="font-display text-xl">
                            Recent Applications
                        </CardTitle>
                        <Link 
                            to="/admin/applications"
                            className="text-sm text-accent hover:text-accent-light flex items-center gap-1 font-medium"
                        >
                            View all
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {stats?.recentApplications?.map((app) => (
                                <div
                                    key={app._id || app.applicationId}
                                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <span className="text-primary font-semibold">
                                                {(app.firstName || '?').charAt(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium text-foreground">
                                                {`${app.firstName} ${app.lastName}`}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {app.applicationType} • {app.applicationId}
                                            </p>
                                        </div>
                                    </div>
                                    <Badge className={statusColors[app.status]}>
                                        {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Applications by Grade */}
                <Card className="border-border">
                    <CardHeader className="pb-4">
                        <CardTitle className="font-display text-xl flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-accent" />
                            By Grade
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {stats?.gradeDistribution?.map((item) => (
                                <div key={item._id} className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 rounded-full bg-accent" />
                                        <span className="text-foreground font-medium">Grade {item._id}</span>
                                    </div>
                                    <span className="text-muted-foreground font-semibold">{item.count}</span>
                                </div>
                            ))}
                        </div>

                        {/* Progress bars */}
                        <div className="mt-6 space-y-3">
                            {stats?.gradeDistribution?.map((item) => {
                                const total = stats?.overview?.total || 1;
                                const percentage = Math.round((item.count / total) * 100);
                                return (
                                    <div key={item._id + '-bar'}>
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-muted-foreground">Grade {item._id}</span>
                                            <span className="text-muted-foreground">{percentage}%</span>
                                        </div>
                                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-accent rounded-full transition-all duration-500"
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Quick Actions */}
            <Card className="border-border">
                <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="font-display text-lg font-semibold text-foreground">
                                Need to review applications?
                            </h3>
                            <p className="text-muted-foreground text-sm mt-1">
                                You have {stats?.overview?.pending || 0} pending applications waiting for review.
                            </p>
                        </div>
                        <Link
                            to="/admin/applications?status=pending"
                            className="px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent-light transition-colors"
                        >
                            Review Now
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminDashboard;
